import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Inject,
  Input,
} from '@angular/core';
import { MicrofrontendComponent } from '../microfrontend.component';
import { AppName, AppUrl } from '../microfrontend.tokens';
import { Params, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import {
  AnalyticsService,
  Source,
} from '../../shared/services/analytics/analytics.service';
import { ToastService } from '../../shared/services/toast/toast.service';
import {
  HIDE_ACCOUNTING_SERVICE_BANNER,
  HIDE_LABOR_SERVICE_BANNER,
} from '../../../types-legacy';
import { StorageService } from '../../shared/services';
import { SubscriptionUpdateReason } from '@web-frontend/shared/services/subscription';
import { SubscriptionViewService } from '@web-frontend/shared/services/subscription-view';

export enum EventType {
  sendAnalyticsEvent = 'sendAnalyticsEvent',
  showToast = 'showToast',
  closeBanner = 'closeBanner',
  closeModal = 'closeModal',
}

type EventPayload = { event: string } | { path: string; params?: Params };

type SendEventPayload =
  | { id: string }
  | { type: 'info' | 'success' | 'error'; text: string; description?: string };

type EventData = {
  type: EventType;
  payload?: EventPayload;
};

type SendEventData = {
  type: EventType;
  payload?: SendEventPayload;
};

export enum ServiceType {
  userPlan = 'userplan',
  addSeats = 'addseats',
  regularPlans = 'regularplans',
  trial = 'trial',
  onboardingPlan = 'onboardingplan',
  paymentSuccess = 'paymentsuccess',
  dowloadInvoicesPlan = 'dowloadinvoicesplan',
}

@Component({
  selector: 'roma-mf-accounting-service',
  template: `<mf-accounting-service
    [attr.mode]="mode"
    [attr.ismodal]="isModal"
    [attr.openplans]="openPlans"
    [attr.updatereason]="updateReason"
    [attr.fromurl]="fromUrl"
  ></mf-accounting-service> `,
  styles: [
    `
      :host(.mf-accounting-service-modal) {
      }

      :host(.mf-accounting-service-list) .mf-accounting-service {
        margin: -40px -40px 15px -32px;
      }

      @media screen and (max-width: 768px) {
        :host(.mf-accounting-service-list) .mf-accounting-service {
          margin: -18px -20px 15px -20px;
        }
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MfAccountingServiceComponent extends MicrofrontendComponent<
  EventData,
  SendEventData
> {
  showAccountingBanner = !JSON.parse(
    StorageService.GetItem(HIDE_ACCOUNTING_SERVICE_BANNER)
  );
  showLaborBanner = !JSON.parse(
    StorageService.GetItem(HIDE_LABOR_SERVICE_BANNER)
  );

  @Input() appName = 'mf-accounting-service';
  @Input() mode: ServiceType;
  @Input() isModal: boolean;
  @Input() openPlans: boolean;
  @Input() updateReason: SubscriptionUpdateReason | null;
  @Input() fromUrl: string = this.getFromUrl();

  destroyed = false;

  @HostBinding('class') get hostClass(): string {
    if (this.destroyed) return '';

    return this.isModal
      ? 'mf-accounting-service-modal'
      : 'mf-accounting-service-list';
  }

  //avoid script loading if banner is hidden
  @Input() appUrl: string = localStorage.getItem('mf-accounting-service');

  academyLink = '';

  constructor(
    private readonly analyticsService: AnalyticsService,
    private readonly toastService: ToastService,
    private changeDetectorRef: ChangeDetectorRef,
    @Inject(AppName) appName: string,
    @Inject(AppUrl) appUrl: string,
    private _httpClient: HttpClient,
    private _el: ElementRef,
    router: Router,
    private subscriptionViewService: SubscriptionViewService
  ) {
    super(_httpClient, _el, router);
    this.handleCustomEvent = this.handleCustomEvent.bind(this);
  }

  onInit(): void {
    this.router.events.subscribe(() => {
      this.fromUrl = this.getFromUrl();
      this.draw();
    });
  }

  private getFromUrl(): string {
    return window.location.pathname + window.location.search;
  }

  private draw() {
    this.changeDetectorRef.detectChanges();
  }

  handleCustomEvent(event: CustomEvent<EventData>): void {
    const { type, payload } = event.detail;
    const eventsActionsMap = {
      [EventType.sendAnalyticsEvent]: this.handleAnalyticsEvent,
      [EventType.showToast]: this.handleShowToast,
      [EventType.closeBanner]: this.handleCloseBanner,
      [EventType.closeModal]: this.handleCloseModal,
    };

    const action = eventsActionsMap[type];
    if (action) {
      action.call(this, payload);
    }
  }

  async handleAnalyticsEvent({
    event,
    sources,
    properties = {},
  }: {
    event: string;
    sources: Source[];
    properties?: { [key: string]: string };
  }): Promise<void> {
    if (this.mode === ServiceType.onboardingPlan) {
      // quick win: avoid sending analytics for onboarding plan to avoid duplicate events
      return;
    }

    this.analyticsService.trackEvent({
      sources: sources,
      eventName: event,
      eventProperties: properties,
    });
  }

  handleShowToast({
    type,
    text,
  }: {
    type: 'info' | 'success' | 'error';
    text: string;
  }): void {
    this.toastService.show({
      text,
      type,
      msDuration: 4000,
    });
  }

  handleCloseBanner(): void {
    this.destroySelf();
  }

  handleCloseModal(): void {
    // Check if we're in a named outlet (modal)
    const currentUrl = this.router.url;
    const isPricingRoute = currentUrl.includes('/pricing');

    if (!isPricingRoute) {
      // Close the modal outlet
      this.subscriptionViewService.closePlanModal();
      return;
    }

    // Handle regular full-page navigation
    if ([ServiceType.userPlan, ServiceType.regularPlans].includes(this.mode)) {
      if (window.location.href.includes(SubscriptionUpdateReason.billing)) {
        this.router.navigateByUrl('/admin/documents/invoices');
      } else {
        window.history.back();
      }
      return;
    }

    if (this.openPlans) this.destroySelf();
  }

  destroySelf(): void {
    this.destroyed = true;
    const hostElem = this._el.nativeElement as HTMLElement;
    hostElem.className = '';
    this.draw();
  }

  onDestroy(): void {}
}
